import { Circle } from 'lucide-react';
import { Slider } from '@/components/atoms/slider';
import {
  RADIUS_VALUES,
  metersToKilometersString,
  radiusIndexToValue,
} from '@/features/locations/utils/radiusHelpers';

interface RadiusSliderProps {
  /** Current stop index into RADIUS_VALUES. */
  index: number;
  /** Fires on every step (live drag). */
  onIndexChange: (index: number) => void;
  /** Optional: fires once on release. Omit for consumers that apply live. */
  onIndexCommit?: (index: number) => void;
  ariaLabel?: string;
}

/**
 * Shared radius selector over the fixed RADIUS_VALUES stops (icon + "Radius"
 * label + current value + tick labels). Used by the add-location panel and the
 * location metrics card so both look and behave identically.
 */
export function RadiusSlider({
  index,
  onIndexChange,
  onIndexCommit,
  ariaLabel = 'Location radius',
}: RadiusSliderProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <Circle className="size-2.5" aria-hidden="true" />
        <span className="text-xs font-medium text-primary-700">Radius</span>
        <span className="ml-auto text-xs text-neutral-500">
          {metersToKilometersString(radiusIndexToValue(index))}
        </span>
      </div>
      <Slider
        aria-label={ariaLabel}
        min={0}
        max={RADIUS_VALUES.length - 1}
        step={1}
        value={[index]}
        onValueChange={(value) => onIndexChange(value[0])}
        onValueCommit={onIndexCommit ? (value) => onIndexCommit(value[0]) : undefined}
      />
      <div className="-mx-1 flex items-center justify-between text-neutral-500 text-[10px]">
        {RADIUS_VALUES.map((step) => (
          <span key={step}>{metersToKilometersString(step)}</span>
        ))}
      </div>
    </div>
  );
}
